import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";

import { Nav, Footer, Reveal } from "@/components/site";
import { findBatch, type TraceRecord } from "@/data/batches";
import { teas as wholesaleTeas } from "@/data/teas";

import heroMist from "@/assets/hero-mist.webp";
import teaLeavesMacro from "@/assets/tea-leaves-macro.webp";
import blackTea from "@/assets/black-tea.webp";
// import whiteTea from "@/assets/white-tea.webp";
// import blendedTea from "@/assets/blended-tea.webp";v
import teaPicker from "@/assets/tea-picker.webp";
import teaPour from "@/assets/tea-pour.webp";
import estate from "@/assets/estate.webp";
import withering from "@/assets/withering.webp";
import worldHeritage from "@/assets/world-heritage.webp";
import heroPour from "@/assets/hero-pour.webp";
import heroPlantation from "@/assets/hero-plantation.webp";
// import leafCutout from "@/assets/leaf-cutout.webp";
import categoryBlackTea from "@/assets/category-black-tea.webp";
import categorySilverTip from "@/assets/category-silver-tip.webp";
import categoryFlavoured from "@/assets/category-flavoured.webp";
import categoryHerbal from "@/assets/category-herbal.webp";

export default function HomePage() {
  // Search params managed by TradeEnquiries component
  const heroSlides = [
  {
    image: heroMist,
    eyebrow: "Ceylon · Est. 1927",
    titleTop: "Every leaf",
    titleItalic: "whispers",
    titleTail: "a story.",
    body: "Handcrafted single-origin Ceylon tea, drawn from the mist-covered mountains of Sri Lanka and blended by hand in small, considered batches.",
    cta: "Explore the Range",
    href: "#ranges",
  },
  {
    image: heroPlantation,
    eyebrow: "Nuwara Eliya · 1,900m",
    titleTop: "Grown above",
    titleItalic: "the clouds,",
    titleTail: "picked by hand.",
    body: "Six microclimates, one island. Our gardens climb from misty valleys to sunlit ridgelines where every harvest tells a different truth.",
    cta: "Visit the Estate",
    href: "#about",
  },
  {
    image: heroPour,
    eyebrow: "The Ritual",
    titleTop: "Slow down.",
    titleItalic: "Pour",
    titleTail: "the moment.",
    body: "An unhurried cup, an amber liquor, and the quiet gravity of doing one thing very well. This is the Mistvale ritual.",
    cta: "Explore Our Ranges",
    href: "#ranges",
  },
  {
    image: teaPicker,
    eyebrow: "First Pluck · 5:42 AM",
    titleTop: "Two leaves",
    titleItalic: "and a bud —",
    titleTail: "nothing more.",
    body: "Four generations of pluckers, one unchanging rule. Only the tenderest new growth ever finds its way into a Mistvale tin.",
    cta: "Trace Your Tea",
    href: "#trace",
  },
  {
    image: estate,
    eyebrow: "The Estate",
    titleTop: "Visit the",
    titleItalic: "mountain",
    titleTail: "that grew your cup.",
    body: "Guided estate journeys through Nuwara Eliya and Uva. Wake with the pluckers, walk the terraces, share the copper table at dawn.",
    cta: "Discover Our Estate",
    href: "#about",
  },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

function Hero() {
  const [i, setI] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [auto, setAuto] = useState(true);
  const [maxSeen, setMaxSeen] = useState(0);
  const reduced = usePrefersReducedMotion();
  const total = heroSlides.length;

  useEffect(() => setMaxSeen((m) => Math.max(m, i)), [i]);

  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (!auto || hovered || hidden || reduced) return;
    const t = setInterval(() => setI((p) => (p + 1) % total), 6500);
    return () => clearInterval(t);
  }, [auto, hovered, hidden, reduced, total]);

  // manual navigation ends the auto-advance
  const go = (n: number) => {
    setAuto(false);
    setI((n + total) % total);
  };

  return (
    <section
      id="top"
      className="relative h-[100svh] w-full overflow-hidden bg-forest"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {heroSlides.map((s, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-[1600ms] ease-out ${
            i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          {/* mount only shown slides plus the next, so hidden slides don't download */}
          {idx <= maxSeen + 1 && (
            <img
              src={s.image}
              alt={s.titleTop + " " + s.titleTail}
              width={1920}
              height={1280}
              decoding="async"
              className={`absolute inset-0 h-full w-full object-cover ${i === idx ? "animate-slow-zoom" : ""}`}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-forest/40 via-forest/10 to-forest/75" />
        </div>
      ))}

      <div className="relative z-10 flex h-full flex-col justify-between px-6 pb-14 pt-40 md:px-14 md:pb-20 text-ivory">
        <div key={i} className="max-w-4xl animate-whisper">
          <div className="eyebrow mb-6 text-gold">{heroSlides[i].eyebrow}</div>
          <h1 className="display-serif text-[15vw] leading-[0.92] md:text-[9vw] lg:text-[8rem]">
            {heroSlides[i].titleTop}
            <br />
            <em className="italic text-gold/90 font-light">{heroSlides[i].titleItalic}</em>{" "}
            {heroSlides[i].titleTail}
          </h1>
        </div>
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <p key={"p" + i} className="max-w-md text-base leading-relaxed text-ivory/85 md:text-lg font-light animate-whisper">
            {heroSlides[i].body}
          </p>
          <a
            href={heroSlides[i].href}
            className="group inline-flex items-center gap-4 border-b border-ivory/40 pb-2 text-[11px] uppercase tracking-[0.28em] transition-all hover:border-gold hover:text-gold"
          >
            {heroSlides[i].cta}
            <span className="transition-transform group-hover:translate-x-2">→</span>
          </a>
        </div>

        {/* Slider controls */}
        <div className="pointer-events-none absolute inset-x-0 bottom-24 flex items-center justify-between px-6 md:px-14">
          <button
            onClick={() => go(i - 1)}
            aria-label="Previous slide"
            className="pointer-events-auto hidden h-12 w-12 items-center justify-center rounded-full border border-ivory/30 text-ivory/80 transition hover:border-gold hover:text-gold md:flex"
          >
            ←
          </button>
          <button
            onClick={() => go(i + 1)}
            aria-label="Next slide"
            className="pointer-events-auto ml-auto hidden h-12 w-12 items-center justify-center rounded-full border border-ivory/30 text-ivory/80 transition hover:border-gold hover:text-gold md:flex"
          >
            →
          </button>
        </div>

        <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center gap-6">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => go(idx)}
              aria-label={`Slide ${idx + 1}`}
              className={`h-px transition-all duration-500 ${
                i === idx ? "w-14 bg-gold" : "w-8 bg-ivory/40 hover:bg-ivory/70"
              }`}
            />
          ))}
        </div>
        <div className="absolute bottom-6 right-6 text-[11px] uppercase tracking-[0.28em] text-ivory/60 md:right-14">
          {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
      </div>
    </section>
  );
}

/* USP bar — sits directly under the hero. Provenance-and-export angle
   (not a wellness/spa angle), so it reads distinct from other tea sites. */
const usps = [
  {
    title: "Plucked Before Dawn",
    body: "Every leaf is off the bush by 6am, while the mist still sits on the terraces.",
    icon: (
      <>
        <line x1="4" y1="18" x2="20" y2="18" />
        <circle cx="12" cy="13" r="3.6" />
        <line x1="12" y1="6.5" x2="12" y2="8.6" />
        <line x1="6.6" y1="9.6" x2="8.1" y2="11" />
        <line x1="17.4" y1="9.6" x2="15.9" y2="11" />
      </>
    ),
  },
  {
    title: "Single Estate, Traced",
    body: "One garden, one elevation — every tin carries a batch code back to the terrace it grew on.",
    icon: (
      <>
        <path d="M3 18.5 L9 8 L13 14.5 L16 10 L21 18.5 Z" />
        <circle cx="16" cy="8.4" r="1.1" fill="currentColor" stroke="none" />
      </>
    ),
  },
  {
    title: "Rainforest Alliance Certified",
    body: "Shade-grown gardens, protected canopy, and soil left to do its own slow work.",
    icon: (
      <>
        <circle cx="12" cy="12" r="8.4" />
        <path d="M8 13.2 C8.7 8.6 13.6 7.4 15.8 8.2 C15.1 12.4 10.9 15.2 8 13.2 Z" />
      </>
    ),
  },
  {
    title: "Small Batch Since 1927",
    body: "Four generations at the same copper table — nothing here is scaled past what one family can taste-test.",
    icon: (
      <path d="M7 4.2 H17 M7 19.8 H17 M7 4.2 C7 9.5 11.2 10.4 12 12 C12.8 10.4 17 9.5 17 4.2 M7 19.8 C7 14.5 11.2 13.6 12 12 C12.8 13.6 17 14.5 17 19.8" />
    ),
  },
];

function UspBar() {
  return (
    <section className="relative z-10 border-b border-forest/10 bg-ivory">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:grid-cols-4 md:gap-8 md:px-14 md:py-16">
        {usps.map((u, i) => (
          <Reveal key={u.title} delay={i * 100}>
            <div className="flex flex-col items-center text-center">
              <svg
                viewBox="0 0 24 24"
                className="h-9 w-9 text-gold"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {u.icon}
              </svg>
              <h3 className="mt-4 eyebrow text-forest">{u.title}</h3>
              <p className="mt-3 text-xs font-light leading-relaxed text-forest/60">{u.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* Filterable range catalogue — we are not a single-tea house, so this
   groups every grade and blend we offer by category. These are catalogue
   entries (grade/variety names), not individually photographed products,
   so each is shown against a category-specific "sample box" illustration
   rather than a real product photo we don't have. No prices or "sold out"
   badges: this is a trade catalogue, not a live storefront. */
type RangeCategory = "Black" | "Blended" | "Flavoured" | "Herbal" | "Artisanal";

const rangeFilters: { key: RangeCategory; label: string }[] = [
  { key: "Black", label: "Black Tea" },
  { key: "Blended", label: "Blended Tea" },
  { key: "Flavoured", label: "Flavoured Tea" },
  { key: "Herbal", label: "Herbal Tea" },
  { key: "Artisanal", label: "Artisanal Tea" },
];

const rangeItems: Record<RangeCategory, string[]> = {
  Black: ["OP", "OP1", "OPA"],
  Blended: [
    "Premium English Breakfast",
    "Blue Pea Tea Blend",
    "Hibiscus Tea",
    "Chamomile Tea Blend",
    "Rose Green Tea Blend",
    "Ginger Tea Blend",
  ],
  Flavoured: ["Strawberry", "Earl Grey", "Apple", "Peach", "Pineapple", "Ginger", "Mint"],
  Herbal: ["Immunity", "Recovery", "Energize", "Detox", "Fine Sleep", "Calm"],
  Artisanal: ["Blooming Tea", "Tea Ball", "Silver Tip", "Gold Tip"],
};

/* One box illustration per category — a wooden export chest for orthodox
   grades, a tin canister for blends, a foil sachet for flavoured teas, an
   apothecary jar for herbal infusions, a ribboned gift box for artisanal
   lots — so the packaging style itself signals what kind of tea it is. */
function RangeBox({ category }: { category: RangeCategory }) {
  const common = {
    viewBox: "0 0 100 100",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (category) {
    case "Black":
      return (
        <svg {...common}>
          <rect x="16" y="32" width="68" height="52" rx="1.5" />
          <line x1="16" y1="46" x2="84" y2="46" />
          <line x1="24" y1="32" x2="24" y2="84" />
          <line x1="76" y1="32" x2="76" y2="84" />
          <rect x="38" y="20" width="24" height="12" rx="1.5" />
        </svg>
      );
    case "Blended":
      return (
        <svg {...common}>
          <path d="M22 28 L22 76 C22 82 34 86 50 86 C66 86 78 82 78 76 L78 28" />
          <ellipse cx="50" cy="28" rx="28" ry="8.5" />
          <path d="M22 40 C22 46 34 50 50 50 C66 50 78 46 78 40" />
        </svg>
      );
    case "Flavoured":
      return (
        <svg {...common}>
          <path d="M32 22 L68 22 L68 27 L74 33 C78 50 78 68 72 84 L28 84 C22 68 22 50 26 33 L32 27 Z" />
          <path d="M36 22 L36 14 L64 14 L64 22" />
          <line x1="27" y1="34" x2="73" y2="34" />
        </svg>
      );
    case "Herbal":
      return (
        <svg {...common}>
          <path d="M34 38 C29 56 29 70 34 82 C36 87 64 87 66 82 C71 70 71 56 66 38 Z" />
          <rect x="41" y="20" width="18" height="18" rx="2" />
          <ellipse cx="50" cy="19" rx="12" ry="3.5" />
          <line x1="30" y1="55" x2="70" y2="55" />
        </svg>
      );
    case "Artisanal":
      return (
        <svg {...common}>
          <rect x="20" y="36" width="60" height="48" rx="1.5" />
          <line x1="50" y1="36" x2="50" y2="84" />
          <line x1="20" y1="58" x2="80" y2="58" />
          <path d="M50 36 C44 24 34 24 34 32 C34 38 44 38 50 36 C56 38 66 38 66 32 C66 24 56 24 50 36 Z" />
        </svg>
      );
  }
}

function FeaturedRange() {
  const [active, setActive] = useState<RangeCategory>("Black");

  return (
    <section id="ranges" className="bg-ivory py-32 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-14">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <div className="eyebrow mb-6 text-moss">— The Full Range</div>
            <h2 className="display-serif text-4xl text-forest md:text-6xl">
              Black, blended,
              <br />
              and everything <em className="italic text-gold">in between.</em>
            </h2>
            <p className="mx-auto mt-6 max-w-lg font-light leading-relaxed text-forest/70">
              We were never a single-tea house. Black, blended, flavoured,
              herbal, and small-lot artisanal teas all leave the same
              estate — every grade traceable to the terrace it grew on.
            </p>
          </div>
        </Reveal>

        <Reveal delay={150}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {rangeFilters.map((f) => (
              <button
                key={f.key}
                onClick={() => setActive(f.key)}
                aria-pressed={active === f.key}
                className={`rounded-full border px-5 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  active === f.key
                    ? "border-forest bg-forest text-ivory"
                    : "border-forest/25 text-forest/65 hover:border-forest/50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div
          key={active}
          className="mt-14 grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-3 lg:grid-cols-4"
        >
          {rangeItems[active].map((name, i) => (
            <Reveal key={name} delay={i * 60} className="group">
              <div className="flex flex-col items-center text-center">
                <div className="flex h-28 w-28 items-center justify-center bg-forest/5 text-forest/70 transition-colors duration-500 group-hover:text-gold md:h-32 md:w-32">
                  <RangeBox category={active} />
                </div>
                <h3 className="mt-4 font-serif text-base text-forest md:text-lg">{name}</h3>
                <span className="mt-1 text-[9px] uppercase tracking-[0.2em] text-forest/45">
                  {rangeFilters.find((f) => f.key === active)?.label}
                </span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <div className="mt-16 text-center">
            <a
              href="#enquiry"
              className="inline-flex items-center gap-3 rounded-full border border-forest px-8 py-3 text-[11px] uppercase tracking-[0.28em] text-forest transition hover:bg-forest hover:text-ivory"
            >
              Request the Full Range →
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* Trade Enquiries — the wholesale contact form, its own section. */
const enquiryIntents = [
  { value: "price-list", label: "Request a price list" },
  { value: "samples", label: "Request sample tins" },
  { value: "general", label: "General enquiry" },
] as const;

function TradeEnquiries() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    country: "",
    tea: searchParams.get("tea") ?? "",
    intent: (searchParams.get("intent") as "price-list" | "samples" | "general") ?? "price-list",
    volume: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const teaParam = searchParams.get("tea");
    const intentParam = searchParams.get("intent");
    if (teaParam || intentParam) {
      // Fire after the router's own scroll-restoration settles, or it
      // gets overwritten back to the top of the page.
      const id = window.setTimeout(() => {
        document.getElementById("enquiry")?.scrollIntoView({ block: "start" });
      }, 80);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [searchParams]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const intentLabel = enquiryIntents.find((i) => i.value === form.intent)?.label ?? "Enquiry";
    const teaName = wholesaleTeas.find((t) => t.slug === form.tea)?.name || form.tea || "General range";
    const subject = `${intentLabel} — ${teaName} (${form.company || form.name})`;
    const body = [
      `Name: ${form.name}`,
      `Company: ${form.company}`,
      `Email: ${form.email}`,
      `Country: ${form.country}`,
      `Tea of interest: ${teaName}`,
      `Indicative volume: ${form.volume || "—"}`,
      "",
      form.message,
    ].join("\n");
    window.location.href = `mailto:hello@mistvale.tea?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
    return false;
  };

  const inputCls =
    "w-full border-b border-forest/25 bg-transparent py-3 font-light text-forest placeholder:text-forest/35 focus:border-gold focus:outline-none";
  const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-forest/60";

  return (
    <section id="enquiry" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-14">
        <div className="grid gap-16 md:grid-cols-12">
          <Reveal className="md:col-span-5">
            <div className="eyebrow mb-6 text-moss">— Trade Enquiries</div>
            <h2 className="display-serif text-4xl text-forest md:text-6xl">
              Tell us about
              <br />
              <em className="italic text-gold">your shelf.</em>
            </h2>
            <p className="mt-8 max-w-md font-light leading-relaxed text-forest/70">
              Price lists, sample tins, private-label runs — write a line and
              we answer within two business days. Or reach us directly at{" "}
              <a href="mailto:hello@mistvale.tea" className="border-b border-gold/50 text-forest transition hover:text-gold">
                hello@mistvale.tea
              </a>
              .
            </p>
          </Reveal>

          <Reveal delay={150} className="md:col-span-7">
            <form onSubmit={submit} className="grid gap-8 md:grid-cols-2">
              <div>
                <label htmlFor="wq-name" className={labelCls}>Name</label>
                <input id="wq-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="wq-company" className={labelCls}>Company</label>
                <input id="wq-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company / brand" />
              </div>
              <div>
                <label htmlFor="wq-email" className={labelCls}>Email</label>
                <input id="wq-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="wq-country" className={labelCls}>Country</label>
                <input id="wq-country" value={form.country} onChange={set("country")} className={inputCls} placeholder="Destination market" />
              </div>
              <div>
                <label htmlFor="wq-intent" className={labelCls}>Enquiry</label>
                <select id="wq-intent" value={form.intent} onChange={set("intent")} className={inputCls}>
                  {enquiryIntents.map((i) => (
                    <option key={i.value} value={i.value}>{i.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="wq-tea" className={labelCls}>Tea of interest</label>
                <select id="wq-tea" value={form.tea} onChange={set("tea")} className={inputCls}>
                  <option value="">Whole range</option>
                  {wholesaleTeas.map((t) => (
                    <option key={t.slug} value={t.slug}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="wq-volume" className={labelCls}>Indicative volume</label>
                <input id="wq-volume" value={form.volume} onChange={set("volume")} className={inputCls} placeholder="e.g. 100 kg per quarter, or 1,000 retail tins" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="wq-message" className={labelCls}>Message</label>
                <textarea id="wq-message" rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Anything else we should know" />
              </div>
              <div className="md:col-span-2 flex items-center justify-between gap-6">
                <button
                  type="submit"
                  className="border-b border-gold pb-2 text-[11px] uppercase tracking-[0.28em] text-forest transition hover:text-gold"
                >
                  Send Enquiry →
                </button>
                {sent && (
                  <p className="font-serif italic text-moss">
                    Your email draft is ready — press send and we'll take it from there.
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* About Us — the brand story, sitting just above the registered
   company details. */
function AboutUs() {
  return (
    <section id="about" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-14">
        <div className="grid gap-16 md:grid-cols-12 md:gap-10">
          <Reveal className="md:col-span-5">
            <div className="eyebrow mb-6 text-moss">— About Us</div>
            <h2 className="display-serif text-4xl text-forest md:text-6xl">
              Four generations,
              <br />
              <em className="italic text-gold">one mountain.</em>
            </h2>
            <p className="mt-8 font-light leading-relaxed text-forest/80">
              Mistvale Tea began on a single garden above Nuwara Eliya, where
              the same families have plucked, withered, and rolled the leaf
              by hand for four generations. We are a small-batch house by
              choice — every tin traceable to the terrace it grew on, every
              blend built by hand at the copper table.
            </p>
            <p className="mt-6 font-light leading-relaxed text-forest/80">
              Today we export that same estate leaf — black, blended,
              flavoured, herbal, and small-lot artisanal — to markets around
              the world, still under the mist, still picked before dawn.
            </p>
          </Reveal>
          <Reveal delay={150} className="md:col-span-6 md:col-start-7">
            <img
              src={estate}
              alt="The Mistvale tea estate in the Nuwara Eliya highlands"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover md:aspect-[4/3]"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* Short film — a single full-bleed autoplaying clip, not a grid of tiles.
   Set `filmVideo` below to a real .mp4/.webm import and it plays muted,
   looped, inline; until then the poster photo runs a slow Ken Burns pan
   so the section reads as intentional rather than a broken video. */
const filmPoster = estate;
const filmVideo: string | undefined = undefined;

function ShortFilms() {
  return (
    <section className="relative h-[85svh] min-h-[560px] overflow-hidden bg-forest text-ivory">
      {filmVideo ? (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster={filmPoster}
        >
          <source src={filmVideo} type="video/mp4" />
        </video>
      ) : (
        <img
          src={filmPoster}
          alt="A short film from the Mistvale estate"
          loading="lazy"
          className="absolute inset-0 h-full w-full animate-ken-burns object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-forest/70 via-forest/25 to-forest/70" />
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
        <Reveal>
          <div className="eyebrow mb-6 text-gold">— Watch</div>
          <h2 className="display-serif text-5xl md:text-7xl">
            Three minutes
            <br />
            <em className="italic text-gold">at the estate.</em>
          </h2>
          <p className="mx-auto mt-6 max-w-md font-light text-ivory/75">
            From first light in the gardens to the last steep of the day — a
            short film shot on the estate.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* Social feed — an Instagram-style grid plus a monoline social-icon row.
   No live feed is wired up (that needs Instagram API credentials), so
   this uses estate photography as a stand-in grid. Hrefs are "#"
   placeholders — swap in real profile URLs once the accounts exist. */
function InstagramGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="17.2" cy="6.8" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}
function FacebookGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M14.5 8.4 h-1.6 c-1 0 -1.6 0.6 -1.6 1.7 v2 h-1.8 M11.3 12.1 h2.4 M12.5 12.1 v6.4" />
    </svg>
  );
}
function PinterestGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.5 17 C10.5 14 11 11.4 11 10.2 C11 8.6 11.9 7.6 13.2 7.6 C14.6 7.6 15.4 8.7 15.4 10.3 C15.4 12.4 14.2 14.4 12.4 14.4 C11.6 14.4 11 14 10.8 13.4" />
    </svg>
  );
}
function LinkedInGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <line x1="8" y1="10.5" x2="8" y2="16" />
      <circle cx="8" cy="7.6" r="0.6" fill="currentColor" stroke="none" />
      <path d="M11.5 16 v-3.4 c0 -1.4 1 -2.1 2.1 -2.1 c1.1 0 1.9 0.7 1.9 2.1 V16" />
    </svg>
  );
}

const socialGrid = [teaLeavesMacro, teaPour, estate, withering, teaPicker, worldHeritage];

const socialLinks = [
  { label: "Instagram", href: "#", icon: <InstagramGlyph className="h-4 w-4" /> },
  { label: "Facebook", href: "#", icon: <FacebookGlyph className="h-4 w-4" /> },
  { label: "Pinterest", href: "#", icon: <PinterestGlyph className="h-4 w-4" /> },
  { label: "LinkedIn", href: "#", icon: <LinkedInGlyph className="h-4 w-4" /> },
];

function SocialFeed() {
  return (
    <section className="bg-cream py-32 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-14">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <div className="eyebrow mb-6 text-moss">— Follow The Estate</div>
            <h2 className="display-serif text-4xl text-forest md:text-6xl">
              Life at <em className="italic text-gold">1,900 metres.</em>
            </h2>
            <p className="mx-auto mt-6 max-w-md font-light text-forest/70">
              Daily glimpses of the gardens, the pluckers, and the copper table — @mistvale.tea
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-3 gap-3 md:grid-cols-6 md:gap-4">
          {socialGrid.map((img, i) => (
            <Reveal key={i} delay={i * 60}>
              <a
                href="#"
                aria-label="View on Instagram"
                className="group relative block aspect-square overflow-hidden bg-forest/5"
              >
                <img
                  src={img}
                  alt="Life at the Mistvale estate"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-forest/0 opacity-0 transition-all duration-500 group-hover:bg-forest/55 group-hover:opacity-100">
                  <InstagramGlyph className="h-6 w-6 text-ivory" />
                </div>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <div className="mt-14 flex items-center justify-center gap-5">
            {socialLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                aria-label={s.label}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-forest/25 text-forest/70 transition hover:border-forest hover:bg-forest hover:text-ivory"
              >
                {s.icon}
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const journalItems = [
  {
    tag: "Journal",
    title: "The Slow Art of Withering",
    date: "3 June 2026",
    excerpt: "Why sixteen patient hours on a bamboo tray shape more flavour than any machine ever could.",
    img: withering,
  },
  {
    tag: "Estate",
    title: "Notes from a Morning in Uva",
    date: "19 May 2026",
    excerpt: "Fog, cold hands, and the particular quiet of a garden before the first basket is full.",
    img: teaPour,
  },
  {
    tag: "Ritual",
    title: "How to Brew a Cup Worth Waiting For",
    date: "2 May 2026",
    excerpt: "Water temperature, timing, and the one mistake that flattens even a great orthodox black.",
    img: blackTea,
  },
];

function Journal() {
  return (
    <section id="journal" className="py-32 md:py-48">
      <div className="mx-auto max-w-7xl px-6 md:px-14">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <div className="eyebrow mb-6 text-moss">— Explore Our World</div>
            <h2 className="display-serif text-4xl text-forest md:text-6xl">
              Stories from
              <br />
              <em className="italic text-gold">the mountain.</em>
            </h2>
          </div>
        </Reveal>
        <div className="mt-16 grid gap-12 md:grid-cols-3 md:gap-8">
          {journalItems.map((it, i) => (
            <Reveal key={it.title} delay={i * 120}>
              <article className="group">
                <div className="overflow-hidden">
                  <img
                    src={it.img}
                    alt={it.title}
                    loading="lazy"
                    className="aspect-[5/6] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                  />
                </div>
                <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-forest/50">
                  <span className="text-gold">— {it.tag}</span>
                  <span>{it.date}</span>
                </div>
                <h3 className="mt-3 font-serif text-2xl text-forest transition-colors group-hover:text-gold">
                  {it.title}
                </h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-forest/70">{it.excerpt}</p>
                <a
                  href="#"
                  className="mt-5 inline-block text-[11px] uppercase tracking-[0.28em] text-forest/70 transition hover:text-gold"
                >
                  Read →
                </a>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={400}>
          <div className="mt-16 text-center">
            <a
              href="#"
              className="inline-flex items-center gap-3 rounded-full border border-forest/30 px-8 py-3 text-[11px] uppercase tracking-[0.28em] text-forest transition hover:border-forest hover:bg-forest hover:text-ivory"
            >
              View All Field Notes →
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}


/* ------------------------------------------------------------------ */
/* Leaf Constellation — real tea shoot with product circles            */
/* ------------------------------------------------------------------ */

const constellation = [
  {
    label: "Black Tea",
    kind: "Orthodox · Fully Oxidised",
    slug: "ceylon-sovereign",
    img: categoryBlackTea,
    x: "78%", y: "18%",
    notes: "Malt · Cocoa · Amber",
    body: "Fully oxidised and orthodox-rolled — the deep amber liquor and brisk, malty character our house is built on.",
    origin: "Ceylon Highlands",
  },
  {
    label: "White Tea",
    kind: "Silver Tip · Unrolled",
    slug: "silver-whisper",
    img: categorySilverTip,
    x: "78%", y: "82%",
    notes: "Honeysuckle · Melon · Silk",
    body: "Only the unopened bud, sun-withered and otherwise untouched — the lightest, rarest grade a garden produces.",
    origin: "High-elevation gardens",
  },
  {
    label: "Flavoured Tea",
    kind: "Scented · Jasmine & Botanical",
    slug: "rose-cardamom-noir",
    img: categoryFlavoured,
    x: "22%", y: "82%",
    notes: "Jasmine · Rose · Bergamot",
    body: "Leaf is layered with fresh blossom or infused with natural oils, taking on their fragrance before it ever reaches a tin.",
    origin: "Small-batch, scented by hand",
  },
  {
    label: "Herbal Tea",
    kind: "Caffeine-Free · Botanical",
    slug: "herbal-infusions",
    img: categoryHerbal,
    x: "22%", y: "18%",
    notes: "Earthy · Warming · Calm",
    body: "No Camellia sinensis at all — rooibos, chamomile, and wildcrafted botanicals steeped for their own character alone.",
    origin: "Wildcrafted & cultivated botanicals",
  },
];


/* Unused component - ConstellationCard
function ConstellationCard({ item }: { item: (typeof constellation)[number] }) {
  return (
    <div className="border border-gold/30 bg-forest/95 p-6 text-left shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] backdrop-blur-sm">
      <div className="flex items-baseline justify-between border-b border-ivory/15 pb-3">
        <div className="eyebrow text-gold">{item.kind}</div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/50">Ceylon</div>
      </div>
      <h3 className="mt-4 font-serif text-2xl text-ivory">{item.label}</h3>
      <p className="mt-1 font-serif italic text-gold/80">{item.notes}</p>
      <p className="mt-4 text-sm font-light leading-relaxed text-ivory/75">{item.body}</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] text-ivory/50">{item.origin}</span>
        <Link
          to={`/tea/${item.slug}`}
          className="pointer-events-auto text-[10px] uppercase tracking-[0.22em] text-gold transition hover:text-ivory"
        >
          Full story →
        </Link>
      </div>
    </div>
  );
}
*/

/* Unused component - LeafConstellation temporarily commented out
function LeafConstellation() {
*/

/* ------------------------------------------------------------------ */
/* Traceability — trace your tea by batch code                         */
/* ------------------------------------------------------------------ */

/* Trace Your Tea + Subscribe, combined into one section, side by side. */
function TraceAndSubscribe() {
  const [code, setCode] = useState("");
  const [record, setRecord] = useState<TraceRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTrace = (e: React.FormEvent) => {
    e.preventDefault();
    const found = findBatch(code);
    if (found) {
      setRecord(found);
      setError(null);
    } else {
      setRecord(null);
      setError("No batch matches that code. Try WL-2401, WL-2402, or WL-2403.");
    }
  };

  const rows: Array<[string, React.ReactNode]> = record
    ? [
        ["Tea", record.tea],
        ["Estate", record.estate],
        ["Elevation", record.elevation],
        ["Plucked", record.plucked],
        ["Processed", record.processed],
        ["Master Blender", record.blender],
        ["Grade", record.grade],
        ["Botanicals", record.botanicals.join(" · ")],
        ["Taster's Note", record.taster],
      ]
    : [];

  return (
    <section id="trace" className="bg-ivory py-32 md:py-48">
      <div className="mx-auto max-w-7xl px-6 md:px-14">
        <div className="grid gap-16 md:grid-cols-2 md:gap-20">
          {/* Left — Trace Your Tea */}
          <Reveal>
            <div className="eyebrow mb-6 text-moss">— Trace Your Tea</div>
            <h2 className="display-serif text-4xl text-forest md:text-5xl">
              Every tin
              <br />
              <em className="italic text-gold">tells the truth.</em>
            </h2>
            <p className="mt-6 font-light leading-relaxed text-forest/70">
              Enter the batch code printed on the base of your tin to trace its
              journey — from the exact garden and elevation it grew on, to the
              hand that blended it and the taster who signed it off.
            </p>

            <form
              onSubmit={handleTrace}
              className="mt-8 flex items-end gap-4 border-b border-forest/30 pb-2"
            >
              <div className="flex-1">
                <label htmlFor="batch" className="block text-[10px] uppercase tracking-[0.22em] text-forest/60">
                  Batch Code
                </label>
                <input
                  id="batch"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g. WL-2401"
                  className="mt-2 w-full bg-transparent py-3 font-serif text-2xl text-forest placeholder:text-forest/30 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="pb-4 text-[11px] uppercase tracking-[0.28em] text-forest transition hover:text-gold"
              >
                Trace →
              </button>
            </form>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] text-forest/50">
              Try WL-2401 · WL-2402 · WL-2403
            </p>

            {error && (
              <p className="mt-6 font-serif italic text-destructive/80">{error}</p>
            )}

            {record && (
              <div className="mt-8 border border-forest/15 bg-cream p-6 animate-whisper md:p-8">
                <div className="flex items-baseline justify-between border-b border-forest/15 pb-4">
                  <div className="eyebrow text-gold">— Batch {record.batch}</div>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-forest/50">
                    Verified · Mistvale Estate
                  </span>
                </div>
                <dl className="mt-6 divide-y divide-forest/10">
                  {rows.map(([k, v]) => (
                    <div
                      key={k}
                      className="grid grid-cols-[auto_1fr] items-baseline gap-6 py-3"
                    >
                      <dt className="min-w-0 text-[10px] uppercase tracking-[0.22em] text-forest/55">
                        {k}
                      </dt>
                      <dd className="min-w-0 font-serif text-lg text-forest">{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-6 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-forest/50">
                  <span>Signed · {record.blender}</span>
                  <span>Ceylon · Origin verified</span>
                </div>
              </div>
            )}
          </Reveal>

          {/* Right — Subscribe */}
          <Reveal delay={200}>
            <div className="border border-forest/15 bg-forest p-8 text-ivory md:p-10">
              <div className="eyebrow mb-6 text-gold">— The Correspondence</div>
              <h2 className="display-serif text-4xl md:text-5xl">
                Letters from
                <br />
                <em className="italic text-gold">the estate.</em>
              </h2>
              <p className="mt-6 font-light leading-relaxed text-ivory/70">
                Occasional dispatches on new harvests, brewing rituals, and
                quiet invitations to the mountain. Never more than once a
                month.
              </p>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="mt-8 flex items-end gap-4 border-b border-ivory/30 pb-2"
              >
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 bg-transparent py-3 text-lg font-light text-ivory placeholder:text-ivory/40 focus:outline-none"
                />
                <button
                  type="submit"
                  className="text-[11px] uppercase tracking-[0.28em] text-gold transition hover:text-ivory"
                >
                  Subscribe →
                </button>
              </form>
              <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-ivory/50">
                Trade buyer?{" "}
                <a href="#enquiry" className="border-b border-gold/50 pb-0.5 text-gold transition hover:text-ivory">
                  Trade Enquiries →
                </a>
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

  return (
    <main className="bg-ivory text-forest">
      <Nav />
      <Hero />
      <UspBar />
      <FeaturedRange />
      <TradeEnquiries />
      <ShortFilms />
      <Journal />
      <SocialFeed />
      <TraceAndSubscribe />
      <AboutUs />
      <Footer />
    </main>
  );
}
